import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, MapPin, Package, Clock, Paperclip, ArrowRight, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { track, EVENTS } from '../lib/analytics';
import { rfqTracking } from '../lib/dataClient';

const TIER_TONE = {
  unverified: 'border-neutral-300 bg-neutral-50 text-neutral-700',
  verified:   'border-neutral-700 bg-neutral-100 text-neutral-900',
  gold:       'border-amber-500 bg-amber-50 text-amber-900',
  elite:      'border-black bg-black text-white',
};

const TIER_LABEL = {
  unverified: 'Listed',
  verified:   'Verified',
  gold:       'Gold',
  elite:      'Elite',
};

const titleCase = (s) => (s || '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmt = (iso) => !iso ? '' : new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

/**
 * QuotesGrid — Renders the structured supplier quotes for an RFQ.
 *
 * Props:
 *   quotes:       Array of quote objects from `get_rfq_tracking().quotes_json`
 *   reference:    RFQ reference code (for analytics + supplier-card deep links)
 *   compact:      boolean — if true, hides notes + attachments + decision UI (public /track view)
 *   canDecide:    boolean — if true, render Accept/Reject buttons (private buyer dashboard, authenticated owner)
 *   rfq:          full RFQ row — passed through to the dataClient.decideOnQuote
 *                 so the post-decision email payloads can be enriched without
 *                 a second roundtrip.
 *   onDecided:    optional callback invoked after a successful decision with
 *                 the RPC payload. Parent should refresh its tracking data.
 */
const QuotesGrid = ({ quotes = [], reference, compact = false, canDecide = false, rfq = null, onDecided }) => {
  // Local UI state, keyed by quote.id, to track which card is open for input
  // and which is currently in-flight. We deliberately keep this state at the
  // grid level (not inside each card) so only one decision dialog can be
  // expanded at a time and the rest stay clean.
  const [openNote, setOpenNote]   = useState({});   // { [quoteId]: { mode: 'accept'|'reject', text: string } }
  const [pendingId, setPendingId] = useState(null);

  if (!Array.isArray(quotes) || quotes.length === 0) {
    return (
      <div data-testid="rfq-quotes-empty" className="mt-4 border border-dashed border-neutral-300 bg-white/40 p-6">
        <p className="text-sm text-neutral-600">
          No quotes yet. Once GIWAHS-assigned factories respond, each quote will appear here with price, MOQ, lead time, and any notes or attachments.
        </p>
      </div>
    );
  }

  const onSupplierClick = (slug) => {
    track(EVENTS.BUYER_QUOTE_SUPPLIER_CLICKED, { reference_code: reference, supplier_slug: slug });
  };

  const startDecision = (quoteId, mode) => {
    setOpenNote((s) => ({ ...s, [quoteId]: { mode, text: s[quoteId]?.text || '' } }));
  };

  const cancelDecision = (quoteId) => {
    setOpenNote((s) => {
      const next = { ...s };
      delete next[quoteId];
      return next;
    });
  };

  const submitDecision = async (quoteId) => {
    const entry = openNote[quoteId];
    if (!entry) return;
    const accept = entry.mode === 'accept';
    setPendingId(quoteId);
    try {
      const result = await rfqTracking.decideOnQuote({
        quoteId,
        accept,
        note: entry.text || null,
        rfq,
      });
      track(accept ? EVENTS.BUYER_QUOTE_ACCEPTED : EVENTS.BUYER_QUOTE_REJECTED, {
        reference_code: reference,
        quote_id: quoteId,
        already: !!result?.already,
      });
      toast.success(accept ? 'Quote accepted — RFQ closed.' : 'Quote rejected.');
      cancelDecision(quoteId);
      if (typeof onDecided === 'function') {
        onDecided(result);
      }
    } catch (err) {
      const msg = err?.message || String(err);
      track(EVENTS.BUYER_QUOTE_DECISION_FAILED, { reference_code: reference, quote_id: quoteId, error: msg.slice(0, 120) });
      if (/not authorized/i.test(msg)) {
        toast.error('You can only accept or reject quotes on your own RFQ. Please sign in with the same email used to submit it.');
      } else {
        toast.error(`Could not record decision: ${msg}`);
      }
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div data-testid="rfq-quotes-grid" className="mt-4 grid gap-3 md:grid-cols-2">
      {quotes.map((q, i) => {
        const tier = q.supplier_tier || 'verified';
        const tierTone = TIER_TONE[tier] || TIER_TONE.verified;
        const tierLabel = TIER_LABEL[tier] || titleCase(tier);
        const isAccepted = q.status === 'accepted';
        const isRejected = q.status === 'rejected';
        const decisionLocked = isAccepted || isRejected;
        const decisionOpen   = openNote[q.id]?.mode;
        const isPending      = pendingId === q.id;

        return (
          <article
            key={q.id || i}
            data-testid={`rfq-quote-card-${i}`}
            data-quote-id={q.id}
            data-supplier-slug={q.supplier_slug}
            data-quote-status={q.status}
            className={`border bg-white p-5 ${isAccepted ? 'border-emerald-700' : isRejected ? 'border-neutral-300 opacity-75' : 'border-black'}`}
          >
            <header className="flex items-start justify-between gap-3 border-b border-neutral-200 pb-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 border px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider ${tierTone}`}>
                    <Award size={10} strokeWidth={1.5} /> {tierLabel}
                  </span>
                  {q.status === 'revised' && (
                    <span className="border border-amber-500 bg-amber-50 px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider text-amber-900">Revised</span>
                  )}
                  {isAccepted && (
                    <span data-testid={`rfq-quote-status-accepted-${i}`} className="inline-flex items-center gap-1 border border-emerald-700 bg-emerald-50 px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider text-emerald-900">
                      <Check size={10} strokeWidth={2} /> Accepted
                    </span>
                  )}
                  {isRejected && (
                    <span data-testid={`rfq-quote-status-rejected-${i}`} className="inline-flex items-center gap-1 border border-neutral-400 bg-neutral-100 px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider text-neutral-700">
                      <X size={10} strokeWidth={2} /> Rejected
                    </span>
                  )}
                </div>
                <h4 className="mt-2 truncate font-display text-lg tracking-tight" data-testid={`rfq-quote-supplier-name-${i}`}>
                  {q.supplier_name || q.supplier_slug}
                </h4>
                {q.supplier_country && (
                  <div className="mt-1 inline-flex items-center gap-1 font-mono-data text-[11px] text-neutral-500">
                    <MapPin size={11} strokeWidth={1.5} /> {titleCase(q.supplier_country)}
                  </div>
                )}
              </div>
              <Link
                to={`/supplier/${q.supplier_slug}`}
                target="_blank"
                onClick={() => onSupplierClick(q.supplier_slug)}
                data-testid={`rfq-quote-supplier-link-${i}`}
                className="shrink-0 inline-flex items-center gap-1 border border-black px-2 py-1 font-mono-data text-[10px] uppercase tracking-wider hover:bg-black hover:text-white"
              >
                Factory <ArrowRight size={10} strokeWidth={1.5} />
              </Link>
            </header>

            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <dt className="label-overline text-neutral-500">Unit price</dt>
                <dd data-testid={`rfq-quote-price-${i}`} className="mt-1 font-display text-lg" style={{ fontWeight: 600 }}>
                  {q.unit_price || '—'}
                </dd>
              </div>
              <div>
                <dt className="label-overline text-neutral-500">MOQ</dt>
                <dd data-testid={`rfq-quote-moq-${i}`} className="mt-1 inline-flex items-center gap-1.5 font-display text-lg" style={{ fontWeight: 600 }}>
                  <Package size={12} strokeWidth={1.5} className="text-neutral-500" />
                  {q.moq ? `${Number(q.moq).toLocaleString()}` : '—'}
                </dd>
              </div>
              <div>
                <dt className="label-overline text-neutral-500">Lead time</dt>
                <dd data-testid={`rfq-quote-lead-${i}`} className="mt-1 inline-flex items-center gap-1.5">
                  <Clock size={12} strokeWidth={1.5} className="text-neutral-500" />
                  {q.lead_time_days ? `${q.lead_time_days} days` : '—'}
                </dd>
              </div>
              <div>
                <dt className="label-overline text-neutral-500">Submitted</dt>
                <dd className="mt-1 font-mono-data text-[11px] text-neutral-600">{fmt(q.created_at)}</dd>
              </div>
            </dl>

            {!compact && q.notes && (
              <div className="mt-3 border-t border-neutral-200 pt-3">
                <span className="label-overline text-neutral-500">Notes</span>
                <p data-testid={`rfq-quote-notes-${i}`} className="mt-1 whitespace-pre-wrap text-sm text-neutral-700">{q.notes}</p>
              </div>
            )}

            {!compact && q.attachments_url && (
              <a
                href={q.attachments_url}
                target="_blank"
                rel="noreferrer"
                data-testid={`rfq-quote-attachment-${i}`}
                className="mt-3 inline-flex items-center gap-1.5 border border-black px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider hover:bg-black hover:text-white"
              >
                <Paperclip size={11} strokeWidth={1.5} /> Open attachment
              </a>
            )}

            {/* Decision panel — only shown on the private buyer dashboard for authenticated owners */}
            {!compact && canDecide && !decisionLocked && (
              <div className="mt-4 border-t border-neutral-200 pt-4">
                {!decisionOpen ? (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => startDecision(q.id, 'accept')}
                      data-testid={`rfq-quote-accept-${i}`}
                      disabled={isPending}
                      className="inline-flex items-center gap-1.5 border border-emerald-700 bg-emerald-700 px-3 py-1.5 font-mono-data text-[11px] uppercase tracking-wider text-white hover:bg-emerald-800 disabled:opacity-50"
                    >
                      <Check size={12} strokeWidth={2} /> Accept quote
                    </button>
                    <button
                      type="button"
                      onClick={() => startDecision(q.id, 'reject')}
                      data-testid={`rfq-quote-reject-${i}`}
                      disabled={isPending}
                      className="inline-flex items-center gap-1.5 border border-black px-3 py-1.5 font-mono-data text-[11px] uppercase tracking-wider hover:bg-black hover:text-white disabled:opacity-50"
                    >
                      <X size={12} strokeWidth={2} /> Reject
                    </button>
                  </div>
                ) : (
                  <div>
                    <label htmlFor={`note-${q.id}`} className="label-overline text-neutral-500">
                      {decisionOpen === 'accept' ? 'Optional note to the factory + sourcing desk' : 'Optional reason (helps the factory improve future quotes)'}
                    </label>
                    <textarea
                      id={`note-${q.id}`}
                      data-testid={`rfq-quote-decision-note-${i}`}
                      value={openNote[q.id]?.text || ''}
                      onChange={(e) => setOpenNote((s) => ({ ...s, [q.id]: { ...s[q.id], text: e.target.value } }))}
                      placeholder={decisionOpen === 'accept'
                        ? 'e.g. Sample fee approved · please confirm payment terms.'
                        : 'e.g. Lead time too long for our launch window.'}
                      rows={3}
                      className="mt-2 w-full border border-black bg-white p-2 font-mono-data text-[12px]"
                    />
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => submitDecision(q.id)}
                        data-testid={`rfq-quote-decision-confirm-${i}`}
                        disabled={isPending}
                        className={`inline-flex items-center gap-1.5 border px-3 py-1.5 font-mono-data text-[11px] uppercase tracking-wider disabled:opacity-50 ${
                          decisionOpen === 'accept'
                            ? 'border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800'
                            : 'border-black bg-black text-white hover:bg-neutral-800'
                        }`}
                      >
                        {isPending ? (<><Loader2 size={12} className="animate-spin" /> Saving…</>) : (
                          decisionOpen === 'accept' ? <><Check size={12} strokeWidth={2} /> Confirm accept</> : <><X size={12} strokeWidth={2} /> Confirm reject</>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelDecision(q.id)}
                        data-testid={`rfq-quote-decision-cancel-${i}`}
                        disabled={isPending}
                        className="border border-neutral-300 px-3 py-1.5 font-mono-data text-[11px] uppercase tracking-wider text-neutral-700 hover:border-black hover:text-black disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                    {decisionOpen === 'accept' && (
                      <p className="mt-2 font-mono-data text-[11px] text-neutral-500">
                        Accepting closes this RFQ and notifies the chosen factory. Any other live quotes will be marked as rejected.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Compact (public /track) view — show a hint instead of buttons */}
            {compact && canDecide === false && !decisionLocked && (
              <p className="mt-3 font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">
                Open the private dashboard to accept or reject.
              </p>
            )}
          </article>
        );
      })}
    </div>
  );
};

export default QuotesGrid;
